import { Injectable, inject } from '@angular/core';
import { Firestore, collection, getDocs, getDoc, doc, query, where, orderBy, startAfter, limit } from '@angular/fire/firestore';
import { Article } from '../entities/article.model';
import { Observable, from } from 'rxjs';
import { PreviewBlock } from '../entities/article_preview.model';

@Injectable({
  providedIn: 'root'
})
export class ArticleService {
  private firestore: Firestore;

  private readonly articlesCollection = 'articles';
  private readonly articlesPreviewBlocksCollection = 'article_previews';

  constructor() {
    this.firestore = inject(Firestore);
  }

  getArticles(language: string): Observable<Article[]> {
    const articlesCollection = collection(this.firestore, 'articles');
    const articlesQuery = query(articlesCollection, where('language', '==', language));
    return from(getDocs(articlesQuery).then(snapshot => 
      snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Article))
    ));
  }

  getArticleById(id: string): Observable<Article | undefined> {
    const articleDocRef = doc(this.firestore, `articles/${id}`);
    return from(getDoc(articleDocRef).then(docSnapshot => 
      docSnapshot.exists() ? ({ id: docSnapshot.id, ...docSnapshot.data() } as Article) : undefined
    ));
  }

  getArticlePreviewBlocks(
    language: string,
    category: string,
    startAfterDate: Date | null = null,
    pageSize: number = 10
  ): Observable<PreviewBlock[]> {
    const previewBlocksCollection = collection(this.firestore, this.articlesPreviewBlocksCollection);
    let previewBlocksQuery;

    if (startAfterDate) {
      previewBlocksQuery = query(
        previewBlocksCollection,
        where('language', '==', language),
        where('category', '==', category),
        orderBy('date_added', 'desc'),
        startAfter(startAfterDate),
        limit(pageSize)
      );
    } else {
      previewBlocksQuery = query(
        previewBlocksCollection,
        where('language', '==', language),
        where('category', '==', category),
        orderBy('date_added', 'desc'),
        limit(pageSize)
      );
    }

    return from(getDocs(previewBlocksQuery).then(snapshot =>
      snapshot.docs.map(doc => (doc.data() as PreviewBlock))
    ));
  }
}
